import React, { useState } from 'react';
import { FaTimes, FaEnvelope, FaUser, FaPhone, FaBuilding, FaSpinner, FaPaperPlane } from 'react-icons/fa';
import API_URL from '../../config/api';
import './ProductEnquiryModal.css';

const ProductEnquiryModal = ({ isOpen, onClose, product, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      setError('Name, Email, and Phone are required fields');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const response = await fetch(API_URL.ENQUIRIES, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          productId: product._id ?? product.id,
          productName: product.name,
          productCategory: product.category,
          type: 'product'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setSuccess('Enquiry sent successfully! Our team will contact you soon.');
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: ''
        });
        // Close modal after 3 seconds
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 3000);
      } else {
        setError(data.message || 'Failed to send enquiry');
      }
    } catch (error) {
      console.error('Error sending enquiry:', error);
      setError(`Error sending enquiry: ${error.message}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: ''
      });
      setError('');
      setSuccess('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="product-enquiry-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <FaEnvelope className="enquiry-icon" />
            <h3>Product Enquiry</h3>
          </div>
          <button className="close-btn" onClick={handleClose} disabled={loading}>
            <FaTimes />
          </button>
        </div>

        <div className="product-info">
          <h4>{product?.name}</h4>
          <p className="product-category">{product?.category}</p>
          <p className="product-sku">SKU: {product?.sku}</p>
        </div>

        <form onSubmit={handleSubmit} className="enquiry-form">
          <div className="form-row">
            <div className="form-group">
              <label>
                <FaUser className="input-icon" />
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="Enter your full name"
              />
            </div>

            <div className="form-group">
              <label>
                <FaEnvelope className="input-icon" />
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="Enter your email address"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>
                <FaPhone className="input-icon" />
                Phone Number *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                placeholder="Enter your phone number"
              />
            </div>

            <div className="form-group">
              <label>
                <FaBuilding className="input-icon" />
                Company Name
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                placeholder="Enter your company name"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Message / Requirements *</label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder="Please describe your requirements or ask any questions about this product..."
              rows="4"
              required
            />
          </div>

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          <div className="form-actions">
            <button type="button" onClick={handleClose} className="cancel-btn" disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? (
                <>
                  <FaSpinner className="spinner" />
                  Sending Enquiry...
                </>
              ) : (
                <>
                  <FaPaperPlane />
                  Send Enquiry
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductEnquiryModal;
